import React, { useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';

import _get from 'lodash/get';

import { Text } from 'reaxl';
import { FilterCheckboxLabel, FilterTitle } from 'reaxl-filters';

import CheckboxWithVisual from '@/components/CheckboxWithVisual';

// Ensure the value used in the component is an array as we assume all FilterCheckboxes are multi-select
const getValue = (values, name) => [].concat(_get(values, name, []));

const getFilterTitle = (value, label) => {
    const valueCount = value.length;
    let filterTitle = label;

    if (valueCount > 0) {
        filterTitle = (
            <>
                {label}
                {' '}
                <Text
                    weight="normal"
                    color="subdued-lighter"
                >
                    {`(${valueCount} selected)`}
                </Text>
            </>
        );
    }

    return (
        <FilterTitle
            key="filter-title"
            title={filterTitle}
        />
    );
};

const updateOptionLabels = (options) => options?.map((option) => ({
    ...option,
    label: <FilterCheckboxLabel
        label={option.label}
        count={option.count}
        moreInfo={option.moreInfo}
        filterBadge={option.filterBadge}
    />,
}));

const CheckboxFilterData = (filter, values) => {
    const { label, name, options: optionsProps } = filter;
    const value = getValue(values, name);
    const options = updateOptionLabels(optionsProps);
    const title = getFilterTitle(value, label);

    return {
        name,
        options,
        value,
        title,
    };
};

/**
 *  FilterCheckboxes renders a group of CheckboxWithVisual elements based off of filter option data
 */
function FilterCheckboxes({
    customFilterOptions,
    filter = {},
    onOptionChange = () => {},
    values = {},
    className,
}) {
    // getting filterCheck
    const filterData = CheckboxFilterData(filter, values);

    // Memoize getFilterOptions function
    const getFilterOptions = useCallback((formattedOptions) => formattedOptions?.map((option) => ({
        ...option,
        label: option.label,
    })), []);

    // Memoize options processing
    const options = useMemo(
        () => customFilterOptions?.(filterData.options) ?? getFilterOptions(filterData.options),
        [customFilterOptions, filterData.options, getFilterOptions]
    );

    // Convert value array to Set for O(1) lookup performance
    const valueSet = useMemo(() => new Set(filterData.value), [filterData.value]);

    // Memoize event handler to prevent recreation
    const handleOptionChange = useCallback((event) => {
        onOptionChange(event, filter);
    }, [onOptionChange, filter]);

    // Memoize checkbox creation with optimized value lookup
    const checkboxes = useMemo(() => (
        options.map((option) => {
            const checked = valueSet.has(option.value);

            const cleanOption = { ...option };
            delete cleanOption.isFiltersCollapsed;
            delete cleanOption.moreInfo;
            delete cleanOption.filterBadge;

            return (
                <CheckboxWithVisual
                    key={option.value}
                    checked={checked}
                    onChange={handleOptionChange}
                    multiple
                    src={option.src}
                    colorValue={option.colorValue}
                    {...cleanOption}
                />
            );
        })
    ), [options, valueSet, handleOptionChange]);

    return (
        <div className={className}>
            <div
                data-cmp="checkboxGroup"
                className="checkbox-group padding-right-1"
            >
                {checkboxes}
            </div>
        </div>
    );
}

FilterCheckboxes.displayName = 'FilterCheckboxes';

FilterCheckboxes.propTypes = {
    /**
     * The className to apply to the root node
     */
    className: PropTypes.string,

    /**
     * standard filter data object
     */
    filter: PropTypes.object,

    /**
     * Callback function fired when an option are changed
     */
    onOptionChange: PropTypes.func,

    /**
     * standard filters values object
     */
    values: PropTypes.object,

    /**
     * custom function to transform filter options
     */
    customFilterOptions: PropTypes.func,
};

export default React.memo(FilterCheckboxes);
